import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mountain, Map, Phone, ChevronRight, TreePine } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getDemoCredentialHints } from '../config/demoCredentials.example';
import { supabase } from '../lib/supabase';
import { arrivedViaRecovery, clearRecoveryArrival } from '../lib/inviteDetection';
import { useHOA } from '../contexts/HOAContext';
import { hoaProfile } from '../lib/hoaProfile';
import { LegalFooter } from '../components/ui';

// Deep links from the invite "You're Already Set Up" card and the reset page:
//   /?login=1&email=…  open the sign-in card with the email pre-filled
//   /?reset=1&email=…  open the card on the "forgot password" form
const readEntryParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const wantsReset = params.get('reset') === '1';
    const wantsLogin = params.get('login') === '1';
    return {
      open: wantsReset || wantsLogin,
      reset: wantsReset,
      email: (params.get('email') || '').trim(),
    };
  } catch (e) {
    return { open: false, reset: false, email: '' };
  }
};

const cardInputClass =
  'w-full pl-12 pr-4 py-3.5 bg-section-bg border border-amber-100/40 hover:border-secondary/40 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-2xl text-primary-dark placeholder-slate-400 text-base font-medium outline-none transition-all';

const FieldIcon = ({ name }) => (
  <span
    className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
    style={{ fontSize: '20px' }}
    aria-hidden="true"
  >
    {name}
  </span>
);

const LandingPage = () => {
  const { hoa } = useHOA();
  const profile = hoaProfile(hoa);
  // Big two-line headline: all but the last word, then the last word.
  const nameWords = profile.shortName.split(/\s+/);
  const headlineTop = nameWords.length > 1 ? nameWords.slice(0, -1).join(' ') : profile.shortName;
  const headlineBottom = nameWords.length > 1 ? nameWords[nameWords.length - 1] : '';
  const [entry] = useState(readEntryParams);
  const [showLoginForm, setShowLoginForm] = useState(entry.open);
  const [email, setEmail] = useState(entry.reset ? '' : entry.email);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(entry.reset);
  const [resetEmail, setResetEmail] = useState(entry.reset ? entry.email : '');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const passwordRef = useRef(null);
  const resetEmailRef = useRef(null);
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();

  // A recovery email that landed on "/" (e.g. its redirect URL was not
  // allow-listed) has already signed the person in: finish at /reset-password.
  useEffect(() => {
    if (!arrivedViaRecovery()) return undefined;
    let active = true;
    const forward = async () => {
      // getSession() resolves only after supabase-js has read the recovery
      // fragment from the URL. Changing the route first would drop it.
      try {
        await supabase.auth.getSession();
      } catch (e) {
        /* ResetPasswordPage shows the expired-link state if there is no session */
      }
      if (!active) return;
      clearRecoveryArrival();
      navigate('/reset-password', { replace: true });
    };
    forward();
    return () => {
      active = false;
    };
  }, [navigate]);

  // Focus the field the deep link is about once the card has animated in.
  useEffect(() => {
    if (!entry.open) return undefined;
    const timer = window.setTimeout(() => {
      if (entry.reset) resetEmailRef.current?.focus();
      else if (entry.email) passwordRef.current?.focus();
    }, 550);
    return () => window.clearTimeout(timer);
  }, [entry]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
      navigate('/portal');
    } catch (err) {
      setError(err.message);
    }
  };

  const closeLoginForm = () => {
    setShowLoginForm(false);
    setEmail('');
    setPassword('');
    setError('');
    setShowReset(false);
    setResetEmail('');
    setResetSent(false);
    setResetError('');
  };

  const handlePasswordReset = async () => {
    setResetError('');
    const target = resetEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      setResetError('Enter the email address you use for the portal.');
      return;
    }
    setResetSending(true);
    try {
      const { error: resetRequestError } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetRequestError) {
        setResetError(resetRequestError.message);
      } else {
        setResetSent(true);
      }
    } catch (err) {
      setResetError('We could not send the reset email. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  const demoCredentials = getDemoCredentialHints();

  return (
    <div className="min-h-screen relative overflow-hidden bg-forest-900 flex flex-col">
      {/* Background: scenic Montana gradient + overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80")' }}
      />
      {/* Deep gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-forest-900/75 via-forest-900/40 to-forest-900/85" />
      {/* Vignette */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, transparent 40%, rgba(13,31,21,0.6) 100%)'
      }} />

      {/* Header */}
      <header className="relative z-20 flex flex-col md:flex-row justify-between items-center gap-4 px-4 md:px-8 py-6 max-w-7xl mx-auto">
        {/* Logo area */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="flex items-center gap-3"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-gold/20 border border-amber-gold/30">
            <Mountain className="w-5 h-5 text-amber-light" />
          </div>
          <div>
            <div className="text-white font-display font-bold text-lg leading-tight tracking-wide drop-shadow-lg">{profile.shortName.toUpperCase()}</div>
            <div className="text-white text-xs tracking-[0.15em] uppercase drop-shadow-lg">{profile.location}</div>
          </div>
        </motion.div>

        {/* Nav */}
        <motion.nav
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center items-center gap-2"
        >
          <Link
            to="/map"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-cream/80 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20 text-sm font-medium transition-all duration-200"
          >
            <Map className="w-4 h-4" />
            Map
          </Link>
          <Link
            to="/contact"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-cream/80 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20 text-sm font-medium transition-all duration-200"
          >
            <Phone className="w-4 h-4" />
            Contact
          </Link>
        </motion.nav>
      </header>

      {/* Hero center content */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-1 min-h-[80vh] px-4 text-center">
        <AnimatePresence mode="wait">
          {!showLoginForm ? (
            <motion.div
              key="hero"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20, scale: 0.96 }}
              transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="flex flex-col items-center"
            >
              {/* Eyebrow */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full bg-forest-800/70 border border-forest-400/50 text-white text-xs tracking-[0.2em] uppercase drop-shadow-md"
              >
                <TreePine className="w-3 h-3" />
                Homeowners Association
              </motion.div>

              {/* Headline */}
              <h1 className="font-display text-6xl md:text-8xl font-bold text-white leading-none mb-4 tracking-tight [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
                {headlineTop}
                {headlineBottom && <br />}
                {headlineBottom && <span className="text-forest-300">{headlineBottom}</span>}
              </h1>

              <p className="text-white/90 text-lg md:text-xl max-w-md mb-10 font-light drop-shadow-lg">
                {profile.landingTagline}
              </p>

              {/* CTA button */}
              <motion.button
                onClick={() => setShowLoginForm(true)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="group min-h-[56px] flex items-center gap-3 px-10 py-4 bg-forest-600 hover:bg-forest-500 border border-forest-400/50 hover:border-forest-300/70 text-white font-semibold rounded-full shadow-warm text-lg transition-all duration-300"
              >
                Access Portal
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>


            </motion.div>
          ) : (
            /* Login Card */
            <motion.div
              key="login"
              initial={{ opacity: 0, scale: 0.88, rotateX: 8 }}
              animate={{ opacity: 1, scale: 1, rotateX: 0 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full max-w-sm"
              style={{ perspective: '1200px' }}
            >
              <div className="relative bg-white rounded-2xl border border-amber-100/30 shadow-warm p-8 text-left">
                {/* Close */}
                <button
                  onClick={closeLoginForm}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full text-slate-400 hover:text-primary-dark hover:bg-section-bg transition-all z-10 flex items-center justify-center"
                  aria-label="Close sign in"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }} aria-hidden="true">close</span>
                </button>

                {/* Header */}
                <div className="mb-7">
                  <div className="w-12 h-12 bg-section-bg rounded-xl flex items-center justify-center mb-4">
                    <span className="material-symbols-outlined text-secondary" style={{ fontSize: '24px' }} aria-hidden="true">
                      {showReset ? 'lock_reset' : 'landscape'}
                    </span>
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{profile.shortName}</p>
                  <h2 className="text-2xl font-bold tracking-tight text-primary-dark mt-1">
                    {showReset ? 'Reset Your Password' : 'Member Portal'}
                  </h2>
                  <p className="text-sm font-medium text-slate-500 mt-1">
                    {showReset
                      ? "We'll email you a secure link to choose a new password."
                      : 'Sign in to access your community account'}
                  </p>
                </div>

                {!showReset ? (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="relative">
                      <FieldIcon name="mail" />
                      <label htmlFor="login-email" className="sr-only">Email</label>
                      <input
                        id="login-email"
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className={cardInputClass}
                        autoComplete="email"
                        required
                      />
                    </div>

                    <div className="relative">
                      <FieldIcon name="lock" />
                      <label htmlFor="login-password" className="sr-only">Password</label>
                      <input
                        id="login-password"
                        ref={passwordRef}
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className={cardInputClass}
                        autoComplete="current-password"
                        required
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setShowReset(true);
                          setResetEmail(email);
                          setResetError('');
                          setResetSent(false);
                        }}
                        className="min-h-[44px] px-2 text-sm font-bold text-secondary hover:underline rounded-full"
                      >
                        Forgot password?
                      </button>
                    </div>

                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="text-red-700 text-sm font-medium bg-red-50 border border-red-200 py-2.5 px-4 rounded-2xl"
                          role="alert"
                        >
                          {error}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.button
                      type="submit"
                      disabled={isLoading}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full min-h-[48px] py-3.5 bg-secondary hover:brightness-110 disabled:opacity-50 text-white font-bold rounded-full shadow-warm transition-all duration-200 text-base"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                          Signing in…
                        </div>
                      ) : 'Sign In'}
                    </motion.button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    {resetSent ? (
                      <div className="bg-section-bg rounded-2xl px-5 py-4" role="status">
                        <p className="text-sm font-bold text-primary-dark">Check your email</p>
                        <p className="text-sm font-medium text-slate-500 mt-1 leading-relaxed">
                          If {resetEmail.trim() || 'that address'} has a portal account, a reset link is on its way. Open it on this device and choose a new password.
                        </p>
                      </div>
                    ) : (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handlePasswordReset();
                        }}
                        className="space-y-4"
                      >
                        <div className="relative">
                          <FieldIcon name="mail" />
                          <label htmlFor="reset-email" className="sr-only">Email</label>
                          <input
                            id="reset-email"
                            ref={resetEmailRef}
                            type="email"
                            placeholder="Email address"
                            value={resetEmail}
                            onChange={e => setResetEmail(e.target.value)}
                            className={cardInputClass}
                            autoComplete="email"
                            required
                          />
                        </div>
                        {resetError && (
                          <p className="text-sm font-medium text-red-700" role="alert">{resetError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={resetSending}
                          className="w-full min-h-[48px] py-3.5 bg-secondary hover:brightness-110 disabled:opacity-50 text-white font-bold rounded-full shadow-warm transition-all text-base"
                        >
                          {resetSending ? 'Sending…' : 'Send Reset Link'}
                        </button>
                      </form>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowReset(false);
                        setResetError('');
                        setResetSent(false);
                        if (!email && resetEmail) setEmail(resetEmail.trim());
                      }}
                      className="w-full min-h-[44px] text-sm font-bold text-secondary hover:underline rounded-full"
                    >
                      Back to Sign In
                    </button>
                  </div>
                )}

                {/* Demo credentials (non-production only) */}
                {!showReset && demoCredentials.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-amber-100/60">
                    <div className="space-y-1.5">
                      {demoCredentials.map((cred, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => { setEmail(cred.email); setPassword(cred.password); }}
                          className="w-full flex items-center justify-between px-4 py-2 bg-section-bg hover:brightness-95 rounded-full transition-all border border-amber-100/40 text-left"
                        >
                          <span className="text-xs font-bold text-primary-dark">{cred.role}</span>
                          <span className="text-slate-500 text-xs font-medium">{cred.email.split('@')[0]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer strip: association line + permanent Terms / Privacy links. In normal
          flow (not absolute) so it never covers the sign-in card on short screens. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="relative z-10 px-4 sm:px-8 py-2"
      >
        <LegalFooter
          tone="dark"
          lead={`© ${new Date().getFullYear()} ${profile.name}${profile.location ? ` — ${profile.location}` : ''}`}
        />
      </motion.div>
    </div>
  );
};

export default LandingPage;
